import { inject, Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  switchMap,
  take,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { User } from '../../domain/models/user';
import { environment } from '../../../../../environments/environment';
import { Store } from '@ngrx/store';
import { selectAuthToken, selectFingerprint } from '../../state/auth.selectors';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { setDeviceFingerprint } from '../../state/auth.actions';
import { clearHomeState } from '../../../home/state/home.actions';
import { AuthStatus } from '../../domain/models/auth-status';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly http = inject(HttpClient);
  private baseUrl = environment.apiUrl;
  private store = inject(Store);
  private readonly FINGERPRINT = 'deviceFingerprint';

  async generateAndStoreFingerprint() {
    const suffix = environment.isSuperAdmin ? '|SA' : '|APP';
    const key = `${this.FINGERPRINT}${suffix}`;

    const saved = localStorage.getItem(key);
    if (saved) {
      this.store.dispatch(setDeviceFingerprint({ fingerprint: saved }));
      return;
    }

    const fp = await FingerprintJS.load();
    const result = await fp.get();

    const fingerprint = `${result.visitorId}${suffix}`;

    localStorage.setItem(key, fingerprint);
    this.store.dispatch(setDeviceFingerprint({ fingerprint }));
  }

  checkAuth(): Observable<AuthStatus> {
    return this.oidcSecurityService.checkAuth().pipe(
      map((res) => {
        if (!res.isAuthenticated || !res.accessToken) {
          const hasStorage =
            !!sessionStorage.getItem('authStorage') ||
            !!localStorage.getItem('authStorage');

          return {
            isAuthenticated: false,
            reason: hasStorage ? 'expired' : 'notLoggedIn',
          };
        }
        return {
          isAuthenticated: true,
          accessToken: res.accessToken,
          reason: 'valid',
        };
      }),
    );
  }

  login() {
    return this.oidcSecurityService.authorize();
  }

  logout(): Observable<void> {
    return new Observable<void>((observer) => {
      this.oidcSecurityService.getIdToken().subscribe((idToken: string) => {
        const postLogoutUri = encodeURIComponent(environment.post_logout_url);
        window.location.href =
          `${environment.url_audaces_id}/connect/endsession?` +
          `id_token_hint=${idToken}&post_logout_redirect_uri=${postLogoutUri}`;

        if (window.sessionStorage) {
          window.sessionStorage.clear();
        }

        const suffix = environment.isSuperAdmin ? '|SA' : '|APP';
        localStorage.removeItem(`${this.FINGERPRINT}${suffix}`);
        localStorage.removeItem('loginTimestamp');
        localStorage.removeItem('forceLogoutFromOtherTabs');

        this.store.dispatch(clearHomeState());
        observer.next();
        observer.complete();
      });
    });
  }

  getToken(): Observable<string | undefined> {
    return this.store.select(selectAuthToken).pipe(take(1));
  }

  refreshToken(): Observable<{ token: string; user: User }> {
    return this.oidcSecurityService.getAccessToken().pipe(
      take(1),
      switchMap((oidcToken) => {
        const headers: Record<string, string> = {
          'X-Skip-Interceptor': 'true',
        };
        if (oidcToken) {
          headers['Authorization'] = `Bearer ${oidcToken}`;
        }
        return this.http.post<{ token: string; user: User }>(
          `${this.baseUrl}api/auth/refresh-token`,
          {},
          { withCredentials: true, headers },
        );
      }),
    );
  }

  async handleLoginCallback() {
    const res = await firstValueFrom(this.oidcSecurityService.checkAuth());
    const fingerprint = await firstValueFrom(
      this.store.select(selectFingerprint),
    );

    if (!res.isAuthenticated || !fingerprint) {
      this.logout();
      throw new Error('AUTHENTICATION_ERROR');
    }

    const token = res.accessToken;
    const loginUrl = environment.isSuperAdmin
      ? 'api/auth/login-super-admin'
      : 'api/auth/login';

    const response = await firstValueFrom(
      this.http
        .post<{ token: string; user: User }>(
          `${this.baseUrl}${loginUrl}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Skip-Interceptor': 'true',
              'X-Device-Fingerprint': fingerprint,
            },
            withCredentials: true,
          },
        )
        .pipe(
          catchError((err) => {
            if (
              err.status === 403 &&
              err.error?.errorCode === 'USER_PENDING_APPROVAL'
            ) {
              throw new Error('USER_PENDING_APPROVAL');
            }

            if (
              err.status === 403 &&
              err.error?.errorCode === 'ACCESS_DENIED'
            ) {
              throw new Error('USER_NO_ACCESS');
            }

            throw err;
          }),
        ),
    );

    if (!response.user?.isActive) {
      throw new Error('USER_DISABLED');
    }

    localStorage.setItem('loginTimestamp', Date.now().toString());
    localStorage.setItem('forceLogoutFromOtherTabs', crypto.randomUUID());

    return response;
  }

  async getSessionIdFromToken(): Promise<string | null> {
    const token = await firstValueFrom(this.store.select(selectAuthToken));
    if (!token) return null;

    try {
      const payloadBase64 = token.split('.')[1];
      const payloadJson = atob(payloadBase64);
      const payload = JSON.parse(payloadJson);

      return payload.session_id || null;
    } catch (err) {
      console.error('Error while decoding JWT', err);
      return null;
    }
  }
}
